import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getProject, getProjectPunchPoints, getProjectDelays } from '../services/projectService';
import { getInspections } from '../services/qualityService';
import { exportProjectPdf, exportProjectXlsx } from '../services/exportService';
import { downloadBlob } from '../utils/downloadFile';
import { formatDateDDMMYY } from '../utils/formatDate';
import useUIStore from '../store/uiStore';
import useProjectStore from '../store/projectStore';
import ProjectProgressPanel from '../components/ProjectProgressPanel';
import ProductionPlanPanel from '../components/ProductionPlanPanel';
import ProjectSchedulePanel from '../components/ProjectSchedulePanel';

const INSPECTION_STATUS_COLORS = {
  pending: 'bg-gray-200 text-gray-800',
  passed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  signed_off: 'bg-blue-100 text-blue-800',
};

const PROJECT_STATUS_COLORS = {
  active: 'bg-gray-100 text-gray-800',
  completed: 'bg-green-100 text-green-800',
  on_hold: 'bg-amber-100 text-amber-800',
  cancelled: 'bg-red-100 text-red-800',
};

const ProjectDetailPage = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [punchPoints, setPunchPoints] = useState([]);
  const [delays, setDelays] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const showToast = useUIStore((s) => s.showToast);
  const { cancelProject, setProjectStatus } = useProjectStore();
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [projectRes, ppRes, delayRes, inspectionRes] = await Promise.all([
          getProject(id),
          getProjectPunchPoints(id),
          getProjectDelays(id),
          getInspections({ project_id: id }),
        ]);
        setProject(projectRes.data.project);
        setPunchPoints(ppRes.data.punch_points || []);
        setDelays(delayRes.data.delays || []);
        setInspections(inspectionRes.data.inspections || []);
      } catch (err) {
        showToast(err.response?.data?.detail || 'Failed to load project', 'error');
      }
      setLoading(false);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return <div className="min-h-screen p-6"><p className="text-gray-500">Loading...</p></div>;
  }

  if (!project) {
    return <div className="min-h-screen p-6"><p className="text-gray-500">Project not found.</p></div>;
  }


  const handleExportPdf = async () => {
    try {
      const res = await exportProjectPdf(id);
      downloadBlob(res.data, `project_${id}_report.pdf`);
    } catch (err) {
      showToast('Failed to export PDF', 'error');
    }
  };

  const handleExportXlsx = async () => {
    try {
      const res = await exportProjectXlsx(id);
      downloadBlob(res.data, `project_${id}_report.xlsx`);
    } catch (err) {
      showToast('Failed to export Excel', 'error');
    }
  };

  const handleRemove = async () => {
    if (!window.confirm(`Remove "${project.name}"? It'll be hidden from active lists but its punch points, delays, and daily entries stay on record.`)) {
      return;
    }
    const result = await cancelProject(id);
    if (result.success) {
      showToast('Project removed', 'success');
      navigate('/projects');
    } else {
      showToast(result.error, 'error');
    }
  };

  const handleSetStatus = async (newStatus, verb) => {
    const result = await setProjectStatus(id, newStatus);
    if (result.success) {
      setProject((p) => ({ ...p, status: newStatus }));
      showToast(`Project ${verb}`, 'success');
    } else {
      showToast(result.error, 'error');
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between">
          <Link to="/projects" className="text-sm text-white hover:underline">&larr; Back to projects</Link>
          <div className="flex gap-3">
            <Link to={`/projects/${id}/edit`} className="text-white hover:underline text-xs font-semibold">Edit</Link>
            <button onClick={handleExportPdf} className="text-white hover:underline text-xs font-semibold">Export PDF</button>
            <button onClick={handleExportXlsx} className="text-white hover:underline text-xs font-semibold">Export Excel</button>
            {project.status === 'active' ? (
              <>
                <button onClick={() => handleSetStatus('completed', 'marked completed')} className="text-white hover:underline text-xs font-semibold">Mark Completed</button>
                <button onClick={() => handleSetStatus('on_hold', 'put on hold')} className="text-white hover:underline text-xs font-semibold">Put On Hold</button>
              </>
            ) : (
              <button onClick={() => handleSetStatus('active', 'reactivated')} className="text-white hover:underline text-xs font-semibold">Reactivate</button>
            )}
            <button onClick={handleRemove} className="text-red-600 hover:underline text-xs font-semibold">Remove</button>
          </div>
        </div>

        <div className="glass-card p-6 mt-3 mb-6">
          {project.parent_project_id && (
            <Link
              to={`/projects/${project.parent_project_id}`}
              className="text-xs text-komal-blue hover:underline"
            >
              &larr; Part of {project.parent_project_name}
            </Link>
          )}
          <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
          <p className="text-gray-500 text-sm mt-1">{project.client_name} — {project.location}</p>
          {project.sub_project_count > 0 && (
            <p className="text-xs text-gray-400 mt-1">
              Groups {project.sub_project_count} sub-project{project.sub_project_count === 1 ? '' : 's'} — see the toggle in Production Progress below for combined costing.
            </p>
          )}

          {/* The Inspections roll-up tile was removed here 2026-10-24 (user
              request) - the full Inspections list further down the page is
              unchanged. */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5 text-sm">
            <div>
              <div className="text-gray-500">Status</div>
              <div className="font-semibold">
                <span className={`text-xs px-2 py-1 rounded font-semibold capitalize ${PROJECT_STATUS_COLORS[project.status] || 'bg-gray-100 text-gray-800'}`}>
                  {project.status?.replace('_', ' ')}
                </span>
              </div>
            </div>
            <div>
              <div className="text-gray-500">Manufacturing Due</div>
              <div className="font-semibold">{formatDateDDMMYY(project.manufacturing_expected_completion_date) || '—'}</div>
            </div>
            <div>
              <div className="text-gray-500">Site Installation Due</div>
              <div className="font-semibold">{formatDateDDMMYY(project.site_installation_expected_completion_date) || '—'}</div>
            </div>
            <div>
              <div className="text-gray-500">Budgeted Cost</div>
              <div className="font-semibold">
                {project.budgeted_cost ? `₹${Number(project.budgeted_cost).toLocaleString()}` : '—'}
              </div>
            </div>
          </div>
        </div>

        <div className="glass-card p-6 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4">Project Schedule</h2>
          <ProjectSchedulePanel projectId={id} />
        </div>

        <div className="glass-card p-6 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4">Production Plan</h2>
          <ProductionPlanPanel projectId={id} />
        </div>

        <div className="glass-card p-6 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4">Production Progress</h2>
          <ProjectProgressPanel projectId={id} />
        </div>

        <div className="glass-card mb-6">
          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">Tasks and Issues ({punchPoints.length})</h2>
          </div>
          {punchPoints.length === 0 ? (
            <p className="text-gray-500 text-sm p-5">No punch points for this project yet.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {punchPoints.map((pp) => (
                <li key={pp.id} className="px-5 py-3 text-sm flex items-center justify-between">
                  <span>{pp.issue_description}</span>
                  <span className="text-xs px-2 py-1 rounded bg-gray-100 capitalize">{pp.status}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="glass-card mb-6">
          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">Delays ({delays.length})</h2>
          </div>
          {delays.length === 0 ? (
            <p className="text-gray-500 text-sm p-5">No delays recorded for this project. 🎉</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {delays.map((d) => (
                <li key={d.id} className="px-5 py-3 text-sm flex items-center justify-between">
                  <span>
                    {d.days_late} day(s) late
                    {d.root_cause_category ? ` — ${d.root_cause_category}` : ''}
                    {d.is_external_cause ? ' (external cause)' : ''}
                  </span>
                  <span className="text-xs px-2 py-1 rounded bg-gray-100 capitalize">{d.status}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="glass-card">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Inspections ({inspections.length})</h2>
            <Link to="/quality/inspections" className="text-sm text-komal-blue hover:underline">View all</Link>
          </div>
          {inspections.length === 0 ? (
            <p className="text-gray-500 text-sm p-5">No inspections recorded for this project yet.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {inspections.map((i) => (
                <li key={i.id} className="px-5 py-3 text-sm flex items-center justify-between">
                  <span>
                    {i.inspection_type}
                    {i.findings ? ` — ${i.findings}` : ''}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded font-semibold capitalize ${INSPECTION_STATUS_COLORS[i.status]}`}>
                    {i.status?.replace('_', ' ')}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailPage;
